import { useEffect, useMemo, useState } from "react";
import { fetchArticles } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";

const CATEGORIES = ["All", "Reviews", "Chef Interviews", "Behind The Kitchen", "Hidden Gems", "Cocktails", "BBQ"];

export default function Articles() {
  const [all, setAll] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles().then(setAll).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (active === "All" ? all : all.filter((a) => a.category === active)),
    [all, active]
  );

  const [hero, ...rest] = filtered;

  return (
    <div data-testid="articles-page" className="bg-sand-50">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">
          The Editorial
        </div>
        <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-sand-900 max-w-4xl">
          Reviews, interviews<br />& the stories<br />behind the plates.
        </h1>

        <div className="mt-12 flex flex-wrap gap-2.5" data-testid="article-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`article-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setActive(c)}
              className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all active:scale-95 ${
                active === c
                  ? "bg-sand-900 text-sand-50 border-sand-900"
                  : "bg-transparent text-sand-900 border-sand-300 hover:border-sand-900"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 aspect-[4/5] bg-sand-100 animate-pulse" />
            <div className="lg:col-span-5 space-y-8">
              {[1, 2, 3].map((i) => <div key={i} className="h-32 bg-sand-100 animate-pulse" />)}
            </div>
          </div>
        ) : !hero ? (
          <p className="py-24 text-center font-serif italic text-2xl text-sand-700">No stories in this category yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
              <div className="lg:col-span-7"><ArticleCard a={hero} variant="feature" index={0} /></div>
              <div className="lg:col-span-5 flex flex-col gap-10">
                {rest.slice(0, 3).map((a, i) => <ArticleCard key={a.id} a={a} index={i + 1} />)}
              </div>
            </div>
            {rest.length > 3 && (
              <>
                <div className="editorial-rule mb-12" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                  {rest.slice(3).map((a, i) => <ArticleCard key={a.id} a={a} variant="feature" index={i} />)}
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
